import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Spin, Switch, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddBranchIndex from './AddBranch';
import ConfirmDialog from './ConfirmDialog';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import {
  deleteBranch,
  refreshStoredBranches,
  updateBranch,
  type BranchResponse,
} from './addBranchApi';

interface EditValues {
  name: string;
  code: string;
  address: string;
  email?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
}

const messageFrom = (error: any): string => {
  const message = error?.response?.data?.message;
  return typeof message === 'string'
    ? message
    : 'Something went wrong. Please try again.';
};

const CODE_LOCKED_HINT = 'Code already used in records';
const LAST_ACTIVE_BRANCH_HINT = 'At least one branch must stay active';
const STATUS_LOCKED_HINT =
  "Status can't be changed: this branch is already used in records";
const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const CODE_EXISTS_MESSAGE = 'Branch code already exists';

// Only the phone numbers the user actually changed go in the update
const changedPhones = (
  branch: BranchResponse,
  values: EditValues
): { phoneNumber1?: string; phoneNumber2?: string } => {
  const next1 = values.phoneNumber1?.trim() ?? '';
  const next2 = values.phoneNumber2?.trim() ?? '';
  return {
    ...(next1 !== (branch.phoneNumber1 ?? '') ? { phoneNumber1: next1 } : {}),
    ...(next2 !== (branch.phoneNumber2 ?? '') ? { phoneNumber2: next2 } : {}),
  };
};

export default function BranchListIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<EditValues>();

  const [branches, setBranches] = useState<BranchResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState<BranchResponse | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  // the branch waiting for the "Delete this branch?" confirmation popup
  const [deleting, setDeleting] = useState<BranchResponse | null>(null);

  const handleAuthError = (status: number | undefined): boolean => {
    if (status === 401) {
      window.localStorage.clear();
      navigate('/sign-in');
      return true;
    }
    return false;
  };

  const loadBranches = useCallback(async () => {
    setIsLoading(true);
    try {
      // Also keeps the branch dropdowns in the rest of the app up to date
      setBranches(await refreshStoredBranches());
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        window.localStorage.clear();
        navigate('/sign-in');
      } else {
        dispatch(
          showErrorMessage(error?.response?.data?.message ?? GENERIC_MESSAGE)
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    void loadBranches();
  }, [loadBranches]);

  const openEdit = (branch: BranchResponse) => {
    setEditing(branch);
    form.setFieldsValue({
      name: branch.name,
      code: branch.code,
      address: branch.address,
      email: branch.email ?? '',
      phoneNumber1: branch.phoneNumber1 ?? '',
      phoneNumber2: branch.phoneNumber2 ?? '',
    });
  };

  const closeEdit = () => {
    if (isSaving) return;
    setEditing(null);
    form.resetFields();
  };

  const showFieldError = (field: string, message: string) => {
    if (
      ![
        'name',
        'code',
        'address',
        'email',
        'phoneNumber1',
        'phoneNumber2',
      ].includes(field)
    )
      return false;
    form.setFields([{ name: field as keyof EditValues, errors: [message] }]);
    return true;
  };

  const onSave = async (values: EditValues) => {
    if (editing === null || isSaving) return;
    setIsSaving(true);
    try {
      const res = await updateBranch(editing._id, {
        name: values.name.trim(),
        // the code is read-only once it is used in records, and is only sent when it changed
        ...(editing.canEditCode !== false && values.code.trim() !== editing.code
          ? { code: values.code.trim() }
          : {}),
        address: values.address.trim(),
        // an empty email cannot be sent (the API validates it), so only send it when filled
        ...((values.email?.trim() ?? '') !== ''
          ? { email: values.email?.trim() }
          : {}),
        // phone numbers are only sent when they changed (an empty value clears the number)
        ...changedPhones(editing, values),
      });
      dispatch(
        showSuccessMessage(res?.message ?? 'Branch updated successfully')
      );
      setEditing(null);
      form.resetFields();
      await loadBranches();
    } catch (error: any) {
      const data = error?.response?.data;
      const status: number | undefined = error?.response?.status;
      const message: string =
        typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;
      const keys: string[] | undefined = data?.validation?.body?.keys;

      if (handleAuthError(status)) return;
      if (status === 403) {
        dispatch(showErrorMessage(message));
      } else if (Array.isArray(keys) && keys.length > 0) {
        const validationMessage: string =
          data?.validation?.body?.message ?? 'Invalid value';
        const shown = keys.filter((key) =>
          showFieldError(key, validationMessage)
        );
        if (shown.length === 0) dispatch(showErrorMessage(validationMessage));
      } else if (data?.field === 'code') {
        showFieldError('code', message);
      } else if (
        typeof data?.field === 'string' &&
        showFieldError(String(data.field), message)
      ) {
        // any other field error from the server, e.g. field "email":
        // "Unable to add this email. Please use a different email" (the address belongs to another organization)
      } else if (message === 'Branch already exists') {
        showFieldError('code', CODE_EXISTS_MESSAGE);
      } else {
        dispatch(showErrorMessage(status === 500 ? GENERIC_MESSAGE : message));
        if (status === 400) {
          // e.g. a record was created after the list loaded: the flags changed, so reload them
          setEditing(null);
          form.resetFields();
          void loadBranches();
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Activate / deactivate a branch. Deactive branches disappear from the branch dropdowns for new records.
  const onToggleStatus = async (branch: BranchResponse, turnOn: boolean) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await updateBranch(branch._id, {
        status: turnOn ? 'active' : 'deactive',
      });
      dispatch(
        showSuccessMessage(res?.message ?? 'Branch updated successfully')
      );
      setIsLoading(false);
      await loadBranches();
    } catch (error: any) {
      setIsLoading(false);
      const status: number | undefined = error?.response?.status;
      if (handleAuthError(status)) return;
      dispatch(showErrorMessage(messageFrom(error)));
      // e.g. "At least one branch must stay active": show the current flags
      if (status === 400) void loadBranches();
    }
  };

  // DELETE /branches/:id archives the branch (it is not a hard delete)
  const onDelete = async (branch: BranchResponse) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await deleteBranch(branch._id);
      dispatch(
        showSuccessMessage(res?.message ?? 'Branch deleted successfully')
      );
      setIsLoading(false);
      await loadBranches();
    } catch (error: any) {
      setIsLoading(false);
      const status: number | undefined = error?.response?.status;
      if (handleAuthError(status)) return;
      dispatch(showErrorMessage(messageFrom(error)));
      // e.g. a record was created after the list loaded: show the current flags
      if (status === 400) void loadBranches();
    }
  };

  const isActive = (branch: BranchResponse): boolean =>
    branch.status !== 'deactive';
  const activeBranchCount = branches.filter(isActive).length;
  // the only branch left that is active: it can never be deactivated or deleted
  const isOnlyActiveBranch = (branch: BranchResponse): boolean =>
    isActive(branch) && activeBranchCount === 1;
  // an active branch that cannot be deactivated: it is the only active one, or the server says so
  // (canDeactivate === false, e.g. the branch code is already used in records)
  const isLastActiveBranch = (branch: BranchResponse): boolean =>
    isActive(branch) &&
    (branch.canDeactivate === false || isOnlyActiveBranch(branch));
  // the tooltip says WHY: the only active branch, or the branch is already in use
  const statusHint = (branch: BranchResponse): string => {
    if (!isLastActiveBranch(branch)) return '';
    return isOnlyActiveBranch(branch)
      ? LAST_ACTIVE_BRANCH_HINT
      : STATUS_LOCKED_HINT;
  };
  const cannotDelete = (branch: BranchResponse): boolean =>
    branch.canDelete === false || isOnlyActiveBranch(branch);
  const deleteHint = (branch: BranchResponse): string => {
    if (!cannotDelete(branch)) return '';
    return isOnlyActiveBranch(branch)
      ? LAST_ACTIVE_BRANCH_HINT
      : CODE_LOCKED_HINT;
  };

  const columns: ColumnsType<BranchResponse> = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    { title: 'Branch name', dataIndex: 'name', key: 'name' },
    { title: 'Code', dataIndex: 'code', key: 'code', width: 90 },
    { title: 'Address', dataIndex: 'address', key: 'address' },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email?: string) => email ?? '-',
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (_, branch) => {
        const phones = [branch.phoneNumber1, branch.phoneNumber2]
          .filter((phone) => phone !== undefined && phone !== '')
          .join(', ');
        return phones === '' ? '-' : phones;
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, branch) => (
        <Tooltip title={statusHint(branch)}>
          <span className="status-toggle">
            <Switch
              checked={isActive(branch)}
              disabled={isLastActiveBranch(branch)}
              onChange={(checked) => {
                void onToggleStatus(branch, checked);
              }}
            />
            <span
              className={`status-toggle__text${
                isActive(branch) ? ' is-active' : ''
              }`}
            >
              {isActive(branch) ? 'Active' : 'Deactive'}
            </span>
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 190,
      render: (_, branch) => (
        <div className="settings__row-actions">
          <Button
            type="text"
            icon={<EditOutlined />}
            className="settings__edit-btn"
            onClick={() => {
              openEdit(branch);
            }}
          >
            Edit
          </Button>
          {cannotDelete(branch) ? (
            <Tooltip title={deleteHint(branch)}>
              <span className="settings__disabled-wrap">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  className="settings__edit-btn"
                  disabled
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              type="text"
              icon={<DeleteOutlined />}
              className="settings__edit-btn"
              onClick={() => {
                setDeleting(branch);
              }}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="settings__card settings__card--wide">
      <div className="settings__head-row">
        <div>
          <p className="settings__title">Branches</p>
          <p className="settings__subtitle">
            View and edit the branches of your organization.
          </p>
        </div>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            setIsAddOpen(true);
          }}
          className="settings__button settings__button--primary"
        >
          Add Branch
        </Button>
      </div>

      <Spin spinning={isLoading} fullscreen />

      <Table<BranchResponse>
        rowKey="_id"
        columns={columns}
        dataSource={branches}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No branches found' }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete branch"
        message={
          <>
            Delete the branch <strong>{deleting?.name}</strong> (
            {deleting?.code}
            )? It is archived and no longer appears in the app.
          </>
        }
        confirmText="Delete"
        onCancel={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          const branch = deleting;
          setDeleting(null);
          if (branch !== null) void onDelete(branch);
        }}
      />

      <Modal
        title="Add Branch"
        open={isAddOpen}
        onCancel={() => {
          setIsAddOpen(false);
        }}
        footer={null}
        destroyOnClose
        centered
        maskClosable={false}
        getContainer={false}
        width={720}
        className="settings__modal"
      >
        <AddBranchIndex
          onCancel={() => {
            setIsAddOpen(false);
          }}
          onAdded={() => {
            setIsAddOpen(false);
            void loadBranches();
          }}
        />
      </Modal>

      <Modal
        title="Edit branch"
        open={editing !== null}
        onCancel={closeEdit}
        footer={null}
        destroyOnClose
        getContainer={false}
        centered
        width={720}
        className="settings__modal"
      >
        <Spin spinning={isSaving} fullscreen />
        <Form
          form={form}
          layout="vertical"
          requiredMark={(label, { required }) => (
            <>
              {label}
              {required && <span className="settings__req">*</span>}
            </>
          )}
          onFinish={onSave}
        >
          <div className="settings__grid">
            <Form.Item
              label="Branch name"
              name="name"
              rules={[
                { required: true, message: 'Please enter branch name' },
                { whitespace: true, message: 'Please enter branch name' },
              ]}
            >
              <Input className="settings__input" />
            </Form.Item>

            <Form.Item
              label="Branch code"
              name="code"
              extra={
                editing?.canEditCode === false
                  ? 'The code is locked: it is already used in records.'
                  : undefined
              }
              normalize={(value: string) =>
                (value ?? '').replace(/[^a-zA-Z]/g, '').toUpperCase()
              }
              rules={[
                { required: true, message: 'Please enter branch code' },
                {
                  pattern: /^[A-Z]{3}$/,
                  message: 'Branch code must be 3 letters (e.g. KRR)',
                },
              ]}
            >
              <Input
                className="settings__input"
                maxLength={3}
                disabled={editing?.canEditCode === false}
              />
            </Form.Item>

            <Form.Item
              className="full"
              label="Branch address"
              name="address"
              rules={[
                { required: true, message: 'Please enter branch address' },
                { whitespace: true, message: 'Please enter branch address' },
              ]}
            >
              <Input className="settings__input" />
            </Form.Item>

            <Form.Item
              className="full"
              label="Email"
              name="email"
              rules={[
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input
                className="settings__input"
                placeholder="branch@company.com"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Phone number 1"
              name="phoneNumber1"
              rules={[
                {
                  pattern: /^[6-9]\d{9}$/,
                  message: 'Enter a valid 10-digit mobile number',
                },
              ]}
            >
              <Input
                className="settings__input"
                placeholder="98765 43210"
                maxLength={10}
                prefix={<PhoneOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Phone number 2"
              name="phoneNumber2"
              rules={[
                {
                  pattern: /^[6-9]\d{9}$/,
                  message: 'Enter a valid 10-digit mobile number',
                },
              ]}
            >
              <Input
                className="settings__input"
                placeholder="98765 43210"
                maxLength={10}
                prefix={<PhoneOutlined />}
              />
            </Form.Item>
          </div>

          <div className="settings__actions">
            <Button
              onClick={closeEdit}
              disabled={isSaving}
              className="settings__button settings__button--ghost"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              className="settings__button settings__button--primary"
            >
              Save changes
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
